import React, { useState } from "react";
import Reveal from "./Reveal";

import { Plus, ChevronRight } from "lucide-react";

const services = [
  {
    id: "influencer-marketing",
    num: "01",
    title: "Influencer Marketing",
    short: "Authentic creator-led growth",
    desc: "We identify, onboard, and manage the right creators for your brand — ensuring every collaboration feels authentic, relevant, and results-driven.",
    bullets: [
      "Creator discovery & vetting",
      "Negotiation & contracts",
      "End-to-end campaign management",
      "Performance reporting",
    ],
  },
  {
    id: "content-production",
    num: "02",
    title: "Content Production",
    short: "Reels, campaigns, branded shoots",
    desc: "From ideation to execution, we create high-quality visual content — including reels, campaigns, and branded shoots — that align with your identity and audience.",
    bullets: [
      "Concept & storyboard",
      "Reel & short-form factory",
      "On-set direction",
      "Edit, color, sound",
    ],
  },
  {
    id: "social-media-management",
    num: "03",
    title: "Social Media Management",
    short: "Always-on presence",
    desc: "We handle your complete social presence — from content planning and posting to engagement and growth — ensuring your brand stays active, consistent, and impactful.",
    bullets: [
      "Content calendars",
      "Daily publishing",
      "Community management",
      "Monthly analytics",
    ],
  },
  {
    id: "brand-strategy",
    num: "04",
    title: "Brand Strategy",
    short: "How you look, sound, and stand",
    desc: "We define how your brand looks, communicates, and positions itself in the market — building a foundation that drives long-term recognition and trust.",
    bullets: [
      "Positioning & narrative",
      "Identity systems",
      "Tone of voice",
      "Brand playbooks",
    ],
  },
  {
    id: "production-shoot-management",
    num: "05",
    title: "Production & Shoot Management",
    short: "End-to-end shoot ops",
    desc: "End-to-end shoot execution — including concept development, model coordination, locations, and post-production — handled seamlessly by our team.",
    bullets: [
      "Casting & talent",
      "Locations & permits",
      "Production design",
      "Post-production",
    ],
  },
];

const Services = () => {
  const [active, setActive] = useState(0);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#0A0A0A] text-white pt-20 md:pt-28 pb-20 md:pb-28"
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-7">
            <p className="flex items-center gap-2 text-[12px] tracking-[0.28em] uppercase font-bold text-[#E60000] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E60000]" />
              What We Do
            </p>
            <Reveal>
              <h2
                data-testid="services-heading"
                className="font-display font-black uppercase text-[40px] sm:text-[56px] md:text-[80px] leading-[0.95] tracking-tighter"
              >
                A complete system,
                <br />
                not a service menu<span className="text-[#E60000]">.</span>
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="border-t border-white/15">
          {services.map((s, i) => {
            const open = active === i;
            return (
              <div
                key={s.id}
                data-testid={`service-row-${s.id}`}
                className={`border-b border-white/15 transition-colors duration-300 ${
                  open ? "bg-[#1a0000]" : "hover:bg-white/[0.04]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActive(open ? -1 : i)}
                  data-testid={`service-toggle-${s.id}`}
                  className="w-full text-left grid grid-cols-12 gap-4 md:gap-6 items-center px-2 md:px-4 py-6 md:py-8"
                >
                  <span className="col-span-2 md:col-span-1 text-[#E60000]">
                    <ChevronRight  size={36} strokeWidth={2.5} />
                  </span>
                  <h3
                    className={`col-span-8 md:col-span-7 font-display font-black uppercase tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[0.95] transition-colors ${
                      open ? "text-[#E60000]" : "text-white"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <span className="hidden md:block col-span-3 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/60">
                    {s.short}
                  </span>
                  <span
                    className={`col-span-2 md:col-span-1 justify-self-end w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border ${
                      open
                        ? "bg-[#E60000] border-[#E60000] text-white"
                        : "border-white/30 text-white"
                    } transition-colors`}
                  >
                    <Plus
                      size={18}
                      className={`transition-transform duration-300 ${open ? "rotate-45" : ""}`}
                    />
                  </span>
                </button>
                <div
                  className={`grid grid-cols-12 gap-6 px-2 md:px-4 overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-out ${
                    open
                      ? "max-h-[500px] opacity-100 pb-8 md:pb-10"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="col-span-12 md:col-start-2 md:col-span-7">
                    <p className="text-base md:text-lg leading-relaxed text-white/85">
                      {s.desc}
                    </p>
                  </div>
                  <ul className="col-span-12 md:col-span-4 grid grid-cols-1 gap-2 md:pl-4 md:border-l md:border-white/15">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-3 text-sm text-white/80"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E60000]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
